// Do'kon nomi build vaqtida REACT_APP_SHOP_NAME orqali beriladi.
// Bo'sh bo'lsa — chek tepasida "Uychikans" yoziladi (logo ishlatilmaydi).
const DEFAULT_BRAND_TEXT = (process.env.REACT_APP_SHOP_NAME || "").trim();

const toNum = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .replace(",", ".")
    .replace(/[^\d.-]/g, "");
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const formatF3 = (value) => toNum(value).toFixed(3).replace(/\.?0+$/, "");
const formatMoney = (value) => `${formatF3(value).replace(".", ",")} so'm`;
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const normalizePayType = (value) => {
  const payType = String(value || "").trim().toLowerCase();
  if (payType === "naxt") return "naqd";
  return payType || "naqd";
};

const payTypeLabel = (value) => {
  const payType = normalizePayType(value);
  if (payType === "nasiya") return "Nasiya";
  if (payType === "karta") return "Karta";
  if (payType === "terminal") return "Terminal";
  return "Naqd";
};

export const getReceiptPrintExtras = (receipt = {}, overrides = {}) => ({
  payType: normalizePayType(overrides.payType ?? receipt?.pay_type),
  dueDate:
    overrides.dueDate ??
    receipt?.due_date ??
    receipt?.nmuddat ??
    receipt?.next_due_date ??
    null,
  inCashSom:
    overrides.inCashSom ??
    overrides.inUzsCash ??
    receipt?.in_uzs_cash ??
    receipt?.cash_som ??
    receipt?.paid_cash_som ??
    0,
  inCardSom:
    overrides.inCardSom ??
    overrides.inUzsCard ??
    receipt?.in_uzs_card ??
    receipt?.card_som ??
    receipt?.paid_card_som ??
    0,
  inTerminalSom:
    overrides.inTerminalSom ??
    overrides.inUsd ??
    receipt?.terminal_som ??
    receipt?.dollar ??
    receipt?.in_terminal_som ??
    0,
  note: overrides.note ?? receipt?.note ?? "",
  brandText: overrides.brandText ?? DEFAULT_BRAND_TEXT,
});

// Chop etiladigan kenglik (mm): 80mm rulon -> 70, 58mm rulon -> 46
const buildPageWidth = (paperMm) => (paperMm === 58 ? 46 : 70);

const buildReceiptCSS = (paperMm) => {
  const pageW = buildPageWidth(paperMm);
  const padX = 3;
  const baseFont = paperMm === 58 ? 10.5 : 11.5;
  const titleFont = paperMm === 58 ? 12.5 : 13.5;

  // Sahifa o'lchami (kenglik x chek uzunligi) #page-size ichida JS bilan aniq beriladi:
  // "size: 70mm auto" brauzerda yaroqsiz — e'tiborsiz qolib, chek 90° burilib chiqardi.
  return `
  @page { margin: 0; }

  html, body {
    width: ${pageW}mm;
    margin: 0 !important;
    padding: 0 !important;
    overflow-x: hidden;
  }

  * { box-sizing: border-box; }

  body {
    font-family: 'DejaVu Sans', Arial, sans-serif;
    font-size: ${baseFont}px;
    color: #111;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .paper {
    width: ${pageW}mm;
    padding: 0 ${padX}mm ${padX}mm;
  }

  /* Logo va qora fon o'rniga oq fonda do'kon nomi (termoprinterda toza chiqadi) */
  .brand-block {
    text-align: center;
    padding: 6px 0 6px;
    margin: 0 0 6px 0;
    width: 100%;
    border-bottom: 1px dashed #999;
  }

  .brand-name {
    color: #000;
    text-align: center;
    font-weight: 800;
    font-size: ${paperMm === 58 ? 18 : 22}px;
    letter-spacing: 1px;
  }

  .h {
    text-align: center;
    font-weight: 700;
    font-size: ${titleFont}px;
    margin: 4px 0 8px;
    letter-spacing: 0.3px;
  }

  .meta {
    font-size: 10.5px;
    text-align: left;
    margin-bottom: 4px;
    color: #333;
    line-height: 1.35;
  }

  .s {
    border-top: 1px dashed #999;
    margin: 6px 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  td {
    padding: 2px 0;
    vertical-align: top;
  }

  td.name {
    width: 62%;
    padding-right: 4px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  td.calc {
    width: 38%;
    text-align: right;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: ${paperMm === 58 ? 10 : 10.8}px;
  }

  .code {
    color: #666;
    font-size: 10px;
    margin-left: 4px;
  }

  .calc-sub {
    color: #555;
    font-size: ${paperMm === 58 ? 9.5 : 10}px;
  }

  .total b {
    font-size: ${paperMm === 58 ? 12.5 : 13.5}px;
  }

  .note-block {
    font-size: 10.5px;
    color: #222;
    background: #f8f8f8;
    padding: 4px 6px;
    border-left: 3px solid #ccc;
    margin-top: 6px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .btns {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    gap: 6px;
  }

  .btn {
    padding: 6px 10px;
    border: 1px solid #333;
    background: #fff;
    cursor: pointer;
    border-radius: 6px;
    font-size: 12px;
  }

  @media print {
    .btns { display: none !important; }
  }
  `;
};

export const renderReceiptHTML = (receipt = {}, extras = {}) => {
  const config = getReceiptPrintExtras(receipt, extras);
  const {
    payType,
    dueDate,
    inCashSom,
    inCardSom,
    inTerminalSom,
    note,
    brandText,
  } = config;

  const items = Array.isArray(receipt.items) ? receipt.items : [];
  const rows = items
    .map((item) => {
      const rawName = item.product_name || item.name || "Noma'lum";
      const name =
        rawName.length > 48
          ? `<span style="font-size:11px">${escapeHtml(rawName)}</span>`
          : escapeHtml(rawName);
      const code = item.series || item.sku || item.code || "";
      const qty = Number(item.qty || item.miqdor || 0);
      const unit = Number(item.unit_price ?? item.price ?? 0);
      const line = Number(item.line_total ?? qty * unit);

      return `
      <tr>
        <td class="name">${name}${code ? `<span class="code">${escapeHtml(code)}</span>` : ""}</td>
        <td class="calc">
          <div>${formatF3(qty)} x ${formatMoney(unit)}</div>
          <div>= ${formatMoney(line)}</div>
        </td>
      </tr>`;
    })
    .join("");

  const createdAt = new Date(receipt.created_at || Date.now());
  const dateLabel = Number.isNaN(createdAt.getTime())
    ? "-"
    : createdAt.toLocaleString("uz-UZ", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

  const subtotalVal = Number(receipt.subtotal ?? 0);
  const discountVal = Number(receipt.discount ?? 0);
  const totalVal = Number(receipt.total ?? 0);
  const buyer = escapeHtml(receipt.buyer_name || "-");
  const checkNo = escapeHtml(receipt.check_no || receipt.id || "-");
  const createdBy = escapeHtml(receipt.created_by_username || "");

  const lines = [
    `<tr><td class="name">To'lov turi</td><td class="calc">${payTypeLabel(payType)}</td></tr>`,
  ];

  if (payType === "nasiya" && dueDate) {
    lines.push(
      `<tr><td class="name">Muddati</td><td class="calc">${escapeHtml(dueDate)}</td></tr>`
    );
  } else {
    if (Number(inCashSom) > 0) {
      lines.push(
        `<tr><td class="name">Naqd (so'm)</td><td class="calc">${formatMoney(inCashSom)}</td></tr>`
      );
    }
    if (Number(inCardSom) > 0) {
      lines.push(
        `<tr><td class="name">Karta (so'm)</td><td class="calc">${formatMoney(inCardSom)}</td></tr>`
      );
    }
    if (Number(inTerminalSom) > 0) {
      lines.push(
        `<tr><td class="name">Terminal (so'm)</td><td class="calc">${formatMoney(inTerminalSom)}</td></tr>`
      );
    }
  }

  if (createdBy) {
    lines.push(`<tr><td class="name">Operator</td><td class="calc">${createdBy}</td></tr>`);
  }

  const noteHtml = String(note || "").trim()
    ? `<div class="note-block"><b>Izoh:</b> ${escapeHtml(note)}</div>`
    : "";

  const brandNameHtml = `<div class="brand-name">${escapeHtml(brandText || "Uychikans")}</div>`;

  const css80 = buildReceiptCSS(80);
  const css58 = buildReceiptCSS(58);

  return `
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Chek</title>
      <style id="receipt-css">${css80}</style>
      <style id="page-size"></style>
    </head>
    <body>
      <div class="paper">
        <div class="brand-block">
          ${brandNameHtml}
        </div>

        <div class="h">SAVDO CHEK</div>
        <div class="meta">
          Sana: ${dateLabel}<br />
          Chek: ${checkNo}<br />
          Haridor: ${buyer}
        </div>
        <div class="s"></div>

        <table>${rows}</table>

        <div class="s"></div>
        <table>
          <tr>
            <td class="name">Jami</td>
            <td class="calc">${formatMoney(subtotalVal)}</td>
          </tr>
          <tr>
            <td class="name">Chegirma</td>
            <td class="calc">${formatMoney(discountVal)}</td>
          </tr>
          <tr class="total">
            <td class="name"><b>Yakuniy</b></td>
            <td class="calc"><b>${formatMoney(totalVal)}</b></td>
          </tr>
        </table>

        <div class="s"></div>
        <table>${lines.join("")}</table>

        ${noteHtml}

        <div class="btns">
          <button class="btn" onclick="print80()">Print 80mm</button>
          <button class="btn" onclick="print58()">Print 58mm</button>
          <button class="btn" onclick="window.close()">Yopish</button>
        </div>

        <script>
          const CSS80 = ${JSON.stringify(css80)};
          const CSS58 = ${JSON.stringify(css58)};
          function setCss(css) {
            const node = document.getElementById("receipt-css");
            if (node) node.textContent = css;
          }
          // Sahifa = qog'oz kengligi x chek uzunligi. Balandlik kenglikdan doim katta,
          // shuning uchun brauzer/printer uni tik (portrait) chiqaradi va burmaydi.
          function fitPage(widthMm) {
            const paper = document.querySelector(".paper");
            const btns = document.querySelector(".btns");
            if (!paper) return;
            const contentPx = (btns ? btns.offsetTop : paper.offsetTop + paper.scrollHeight) - paper.offsetTop;
            const contentMm = Math.ceil((contentPx * 25.4) / 96) + 6;
            const heightMm = Math.max(contentMm, widthMm + 20);
            document.getElementById("page-size").textContent =
              "@page { size: " + widthMm + "mm " + heightMm + "mm; margin: 0; }";
          }
          function doPrint() {
            setTimeout(() => window.print(), 40);
          }
          function print80() {
            setCss(CSS80);
            fitPage(${buildPageWidth(80)});
            doPrint();
          }
          function print58() {
            setCss(CSS58);
            fitPage(${buildPageWidth(58)});
            doPrint();
          }
          fitPage(${buildPageWidth(80)});
          window.addEventListener("load", () => fitPage(${buildPageWidth(80)}));
        </script>
      </div>
    </body>
  </html>`;
};

export const openReceiptWindow = (receipt, extras = {}) => {
  const html = renderReceiptHTML(receipt, extras);
  const win = window.open("", "_blank", "width=420,height=740");
  if (win) {
    win.document.write(html);
    win.document.close();
    return;
  }

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "-10000px";
  iframe.style.bottom = "-10000px";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
};
